import ListLayout from 'app/components/Layout/ListLayout';
import { getCurrentNav } from 'app/config/routes';
import React, { useEffect, useState } from 'react';
import classes from './InspectionList.module.scss';
import { Space, Tabs, Badge, Button } from 'antd';
import { InspectionTabs, convertDateToMoment } from './constants';
import NAVIGATE_URL from 'app/config/navigate-url';
import {
  useLazyGetInspectionsQuery,
  useLazyGetInspectorWithJobQuery
} from 'app/features/inspection/api';
import { get, isEmpty, omit } from 'lodash';
import { ExportData } from './components/ExportData';
import { useSelector } from 'react-redux';
import {
  inspectionJobSelector,
  inspectionSelector
} from 'app/features/inspection/slice';

export default function InspectionList() {
  const inspectionJobState = useSelector(inspectionJobSelector);

  const [fetch, result] = useLazyGetInspectionsQuery();
  const [fetchInspector, resultInspector] = useLazyGetInspectorWithJobQuery();

  const [keywordInspection, setKeywordInspection] = useState('');
  const [keywordInspector, setKeywordInspector] = useState('');
  const [currentTab, setCurrentTab] = useState('inspection-job');

  const nav = getCurrentNav();

  const handleCreateNewJob = () => {
    window.open(NAVIGATE_URL.QMAS_CREATE_JOB, '_blank');
  };

  const handleInspectionChange = (filter, sort, pagination) => {
    let paramsFilter = {};
    let paramsSort = {};

    const { searchBy, keywords } = result.originalArgs || {};
    Object.keys(filter).forEach((key) => {
      const payload = filter[key];
      if (!payload.includes('all')) {
        paramsFilter = {
          ...paramsFilter,
          [key]: payload
        };
      }
    });

    if (!isEmpty(sort)) {
      paramsSort = {
        sortBy: `${sort.value}:${sort.direction}`
      };
      if (Array.isArray(sort.range) && sort.range.length > 1) {
        //Have A Range Date.
        paramsSort[`${sort.value}From`] = sort.range[0]?.format('YYYY-MM-DD');
        paramsSort[`${sort.value}To`] = sort.range[1]?.format('YYYY-MM-DD');
        paramsSort['range'] = [
          sort.range[0]?.format('YYYY-MM-DD'),
          sort.range[1]?.format('YYYY-MM-DD')
        ];
      }
    }
    fetch({
      searchBy,
      keywords,
      ...paramsFilter,
      ...paramsSort,
      current: pagination.current,
      pageSize: pagination.pageSize
    });
  };

  const handleInspectorChange = (filter, sort, pagination) => {
    const paramsFilter = {};
    Object.keys(filter).forEach((key: string) => {
      const item = get(filter, key);
      if (item.length && !item.includes('all')) {
        paramsFilter[key] = item;
      }
      if (item.includes('all')) {
        delete paramsFilter[key];
      }
    });
    fetchInspector({
      ...resultInspector.originalArgs,
      ...paramsFilter,
      ...pagination
    });
  };

  const getTabProps = (tab) => {
    if (tab === 'inspection-job') {
      const pagination = get(result, 'data.pagination');

      return {
        keywords: keywordInspection,
        data: get(result, 'data.data', []),
        pagination: {
          pageSize: 10,
          current: pagination?.page || 1,
          ...pagination
        },
        loading: result.isLoading || result.isFetching,
        onChange: handleInspectionChange,
        filtered: inspectionJobState.filter,
        sorter: {
          ...inspectionJobState.sorter,
          range: convertDateToMoment(inspectionJobState.sorter.range)
        }
      };
    }
    const pagination = get(result, 'data.pagination');
    return {
      data: get(resultInspector, 'data.data', []),
      pagination: {
        current: pagination?.page || 1,
        pageSize: 10,
        ...get(resultInspector, 'data.pagination')
      },
      loading: resultInspector.isLoading || resultInspector.isFetching,
      keywords: keywordInspector,
      onChange: handleInspectorChange
    };
  };

  const getPlaceholderByTab = () => {
    if (currentTab === 'inspection-job') {
      return 'Search by Job ID, Product Name, Item No., Supplier Name/Id, Factory Name/Id & PO no.';
    }
    return 'Search by Inspector name';
  };

  const handleSearchInspection = (keyword) => {
    if (keyword) {
      fetch({
        ...result.originalArgs,
        keywords: keyword,
        searchBy: 'All',
        current: 1
      });
    } else {
      fetch({
        ...omit(result.originalArgs, ['searchBy', 'keywords']),
        current: 1
      });
    }
  };

  const handleSearchInspector = (keyword) => {
    if (keyword) {
      fetchInspector({
        ...resultInspector.originalArgs,
        keywords: keyword,
        searchBy: 'inspectorName',
        current: 1
      });
    } else {
      fetchInspector({
        ...omit(resultInspector.originalArgs, ['searchBy', 'keywords']),
        current: 1
      });
    }
  };

  const handleSearch = (keyword) => {
    if (currentTab === 'inspection-job') {
      handleSearchInspection(keyword);
    } else {
      handleSearchInspector(keyword);
    }
  };

  useEffect(() => {
    handleInspectionChange(
      inspectionJobState.filter,
      {
        ...inspectionJobState.sorter,
        range: convertDateToMoment(inspectionJobState.sorter.range)
      },
      {
        current: inspectionJobState?.pagination?.current || 1,
        pageSize: inspectionJobState?.pagination?.pageSize || 10
      }
    );
  }, []);

  return (
    <ListLayout
      title={nav.title}
      icon={
        nav.icon
          ? React.cloneElement(nav.icon as any, {
              className: classes.icon
            })
          : null
      }
      showCreate={false}
      onSearch={handleSearch}
      keyword={getTabProps(currentTab).keywords}
      searchInputProps={{
        placeholder: getPlaceholderByTab(),
        value: getTabProps(currentTab).keywords,
        onChange: (e) => {
          if (currentTab === 'inspection-job') {
            setKeywordInspection(e.target.value);
          } else {
            setKeywordInspector(e.target.value);
          }
        }
      }}
    >
      <Tabs
        activeKey={currentTab}
        animated={{ inkBar: true, tabPane: true }}
        items={InspectionTabs.map((tab) => {
          const tabProp = getTabProps(tab.key);
          return {
            ...tab,
            label: (
              <Space>
                {tab.label}
                <Badge
                  style={{
                    background: '#1890ff'
                  }}
                  overflowCount={9999}
                  count={
                    tab.key !== 'inspection-job' ? 0 : tabProp.pagination.total
                  }
                />
              </Space>
            ),
            children: (
              <>
                {React.cloneElement(tab.children, {
                  ...tabProp
                })}
              </>
            )
          };
        })}
        onChange={(key) => setCurrentTab(key)}
        tabBarExtraContent={{
          right: (
            <Space>
              <ExportData />
              <Button type="primary" size="large" onClick={handleCreateNewJob}>
                Create Job
              </Button>
            </Space>
          )
        }}
      ></Tabs>
    </ListLayout>
  );
}
